import React, { useState, useEffect } from 'react';
import { Search, Edit2, Copy, Trash2, Link2, ExternalLink, Loader2, Eye, FileText } from 'lucide-react';
import useFeedbackManagementStore from '@/stores/AdminStore/FeedbackManagementStore';
import ViewQuestionsModal from './ViewQuestionsModal';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import ErrorDisplay from '@/Components/common/ErrorDisplay';
import './FormLibrary.css';

interface FormLibraryProps {
  onEditForm?: (formId: string) => void;
}

const FormLibrary: React.FC<FormLibraryProps> = ({ onEditForm }) => {
  const { surveys, loading, error, fetchSurveys, deleteForm, setEditingFormId, fetchFormById, currentForm } = useFeedbackManagementStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewQuestionsModalOpen, setViewQuestionsModalOpen] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (!surveys) {
      fetchSurveys();
    }
  }, [surveys, fetchSurveys]);

  const filteredSurveys = surveys?.filter((survey) => {
    const matchesSearch =
      survey.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      survey.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || survey.type.toLowerCase() === typeFilter;
    const matchesStatus = statusFilter === 'all' || survey.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleCopyLink = async (slug: string | undefined, surveyId: string) => {
    if (!slug) return;
    const publicUrl = `${window.location.origin}/feedback/${slug}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedId(surveyId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleEdit = (formId: string) => {
    setEditingFormId(formId);
    if (onEditForm) {
      onEditForm(formId);
    }
  };

  const handleDeleteClick = (formId: string, surveyName: string) => {
    setSurveyToDelete({ id: formId, name: surveyName });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!surveyToDelete) return;

    setDeleting(true);
    const result = await deleteForm(surveyToDelete.id);
    setDeleting(false);

    if (result.success) {
      setDeleteModalOpen(false);
      setSurveyToDelete(null);
    } else {
      alert(result.error || 'Failed to delete form');
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSurveyToDelete(null);
  };

  const handleOpenPublicForm = (slug: string | undefined) => {
    if (!slug) return;
    window.open(`${window.location.origin}/feedback/${slug}`, '_blank');
  };

  const handleViewQuestions = async (formId: string, surveyName: string) => {
    setSelectedSurvey({ id: formId, name: surveyName });
    await fetchFormById(formId);
    setViewQuestionsModalOpen(true);
  };

  const handleCloseViewQuestions = () => {
    setViewQuestionsModalOpen(false);
    setSelectedSurvey(null);
  };

  if (error && !surveys) {
    return (
      <div className="admin-feedback-form-library">
        <ErrorDisplay
          title="Failed to load surveys"
          message={error}
          type="error"
          showRetry={true}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  if (loading && !surveys) {
    return (
      <div className="admin-feedback-form-library">
        <LoadingSpinner size="lg" message="Loading surveys..." />
      </div>
    );
  }

  return (
    <div className="admin-feedback-form-library">
      <div className="admin-feedback-library-header">
        <h3>Survey Library</h3>
        <p>Manage all your surveys and feedback forms</p>

        <div className="admin-feedback-library-filters">
          <div className="admin-feedback-search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search surveys..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="admin-feedback-filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Type</option>
            <option value="feedback">Feedback</option>
            <option value="satisfaction">Satisfaction</option>
            <option value="assessment">Assessment</option>
          </select>

          <select
            className="admin-feedback-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <div className="admin-feedback-library-table-container">
        {filteredSurveys?.length === 0 ? (
          <div className="admin-feedback-empty-state">
            <p>No surveys found</p>
            <span>Create a new form in the Form Builder tab</span>
          </div>
        ) : (
          <table className="admin-feedback-library-table">
            <thead>
              <tr>
                <th>SURVEY</th>
                <th>TYPE</th>
                <th>STATUS</th>
                <th>EXPIRY DATE</th>
                <th>CREATED</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredSurveys?.map((survey) => (
                <tr key={survey.id}>
                  <td>
                    <div className="admin-feedback-survey-info">
                      <div className="admin-feedback-survey-icon">
                        <FileText size={24} />
                      </div>
                      <div>
                        <div className="admin-feedback-survey-name">{survey.name}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-feedback-type-badge admin-feedback-type-badge-${survey.type.toLowerCase()}`}>
                      {survey.type}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-feedback-status-badge admin-feedback-status-badge-${survey.status}`}>
                      {survey.status}
                    </span>
                  </td>
                  <td>
                    <div className="admin-feedback-expiry-date">
                      {survey.expiryDate || 'No expiry'}
                    </div>
                  </td>
                  <td>
                    <div className="admin-feedback-created-date">
                      {survey.created}
                    </div>
                  </td>
                  <td>
                    <div className="admin-feedback-action-icons">
                      {survey.status === 'active' && survey.slug && (
                        <>
                          <button
                            className={`admin-feedback-icon-btn admin-feedback-copy-btn ${copiedId === survey.id ? 'copied' : ''}`}
                            title={copiedId === survey.id ? 'Copied!' : 'Copy Public Link'}
                            onClick={() => handleCopyLink(survey.slug, survey.id)}
                          >
                            {copiedId === survey.id ? <Copy size={16} /> : <Link2 size={16} />}
                          </button>
                          <button
                            className="admin-feedback-icon-btn"
                            title="Open Public Form"
                            onClick={() => handleOpenPublicForm(survey.slug)}
                          >
                            <ExternalLink size={16} />
                          </button>
                        </>
                      )}
                      <button
                        className="admin-feedback-icon-btn"
                        title="View Questions"
                        onClick={() => handleViewQuestions(survey.id, survey.name)}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="admin-feedback-icon-btn"
                        title="Edit"
                        onClick={() => handleEdit(survey.id)}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="admin-feedback-icon-btn admin-feedback-icon-btn-delete"
                        title="Delete"
                        onClick={() => handleDeleteClick(survey.id, survey.name)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* View Questions Modal */}
      <ViewQuestionsModal
        isOpen={viewQuestionsModalOpen}
        onClose={handleCloseViewQuestions}
        surveyName={selectedSurvey?.name || ''}
        questions={currentForm?.questions || []}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="admin-feedback-modal-overlay" onClick={handleCancelDelete}>
          <div className="admin-feedback-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-feedback-modal-header">
              <h3>Delete Survey</h3>
            </div>
            <div className="admin-feedback-modal-body">
              <p>Are you sure you want to delete "{surveyToDelete?.name}"?</p>
              <p className="admin-feedback-modal-warning">This action cannot be undone.</p>
            </div>
            <div className="admin-feedback-modal-footer">
              <button
                className="admin-feedback-modal-btn admin-feedback-modal-btn-cancel"
                onClick={handleCancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="admin-feedback-modal-btn admin-feedback-modal-btn-delete"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 size={16} className="admin-feedback-spinner" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormLibrary;
